/**
 * FlowNotes API Client - Core
 * Core authentication, URL handling, and error detection utilities
 */

import { supabase, isAuthConfigured, getCachedAccessToken, setCachedAccessToken } from '@/lib/supabase';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://api.maater.localhost';

/** True when the API is running locally. Used to gate local-only features
 *  like filesystem polling and external file change detection. */
export const IS_LOCAL_API = API_BASE_URL.includes('localhost');

/** UUID v4 pattern – documents.id in Postgres */
export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

/** Resolve a URL: relative paths starting with / get API_BASE_URL prepended; absolute URLs pass through. */
export function resolveUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE_URL}${url}`;
}

const DB_UNAVAILABLE_RESPONSE = JSON.stringify({ error: 'database_unavailable' });
let dbUnavailableUntilMs = 0;
let lastDbUnavailableLogMs = 0;

const API_DEGRADED_BACKOFF_MS = 8000;

/** True while fetchWithAuth is short-circuiting after a recent 503/502/network failure. */
export function isApiDbCircuitOpen(): boolean {
  return Date.now() < dbUnavailableUntilMs;
}

function openApiDegradedCircuit(reason: string): void {
  dbUnavailableUntilMs = Date.now() + API_DEGRADED_BACKOFF_MS;
  if (Date.now() - lastDbUnavailableLogMs > 10_000) {
    lastDbUnavailableLogMs = Date.now();
    console.warn(`[API] ${reason}; backing off requests for ${API_DEGRADED_BACKOFF_MS / 1000}s`);
  }
}

function dbUnavailableCircuitResponse(): Response {
  return new Response(DB_UNAVAILABLE_RESPONSE, {
    status: 503,
    headers: { 'Content-Type': 'application/json' },
  });
}

/** Intentional cancel (Cmd+K debounce, navigation). Not an outage. */
export function isAbortFetchError(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

async function attachBearer(headers: Headers, isRetry = false): Promise<boolean> {
  if (!supabase) return false;
  const cached = getCachedAccessToken();
  if (cached) {
    headers.set('Authorization', `Bearer ${cached}`);
    return true;
  }
  let session;
  try {
    ({ data: { session } } = await supabase.auth.getSession());
  } catch (err) {
    if (isAbortFetchError(err) && !isRetry) {
      return attachBearer(headers, true);
    }
    throw err;
  }
  if (session?.access_token) {
    setCachedAccessToken(session.access_token);
    headers.set('Authorization', `Bearer ${session.access_token}`);
    return true;
  }
  return false;
}

/** Shared across concurrent 401s so a wake-from-sleep burst triggers one refresh, not one per poller. */
let freshTokenInflight: Promise<string | null> | null = null;

function getFreshAccessToken(rejectedToken: string | null): Promise<string | null> {
  if (freshTokenInflight) return freshTokenInflight;
  freshTokenInflight = (async () => {
    if (!supabase) return null;
    const { data: { session } } = await supabase.auth.getSession();
    let token = session?.access_token ?? null;
    if (token && token === rejectedToken) {
      const { data } = await supabase.auth.refreshSession();
      token = data.session?.access_token ?? null;
    }
    setCachedAccessToken(token);
    return token;
  })()
    .catch(() => null)
    .finally(() => {
      freshTokenInflight = null;
    });
  return freshTokenInflight;
}

/** When Supabase auth is configured, include Bearer token so API can resolve current user/org. Exported for use by components that call the API directly. */
export async function fetchWithAuth(url: string, init?: RequestInit): Promise<Response> {
  if (Date.now() < dbUnavailableUntilMs) {
    return dbUnavailableCircuitResponse();
  }

  const headers = new Headers(init?.headers);
  let hasToken = await attachBearer(headers);
  // Avoid hitting Railway with no Bearer (chat pane mounts before session hydrates).
  if (isAuthConfigured && supabase && !hasToken) {
    return new Response(JSON.stringify({ error: 'Authentication required' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' },
    });
  }
  let response: Response;
  try {
    response = await fetch(resolveUrl(url), { ...init, headers });
  } catch (err) {
    // AbortError is intentional (palette debounce / tab close) - never trip the
    // degraded circuit or the next keystroke shows fake database_unavailable.
    if (!isAbortFetchError(err) && isOfflineOrUnreachableFetchError(err)) {
      openApiDegradedCircuit('API unreachable (network/CORS)');
    }
    throw err;
  }
  // Expired/rotated token (e.g. after sleep): retry once with a fresh session, bypassing the cache.
  if (isAuthConfigured && supabase && response.status === 401) {
    const rejectedToken = headers.get('Authorization')?.replace(/^Bearer /, '') ?? null;
    const freshToken = await getFreshAccessToken(rejectedToken);
    const retryHeaders = new Headers(init?.headers);
    if (freshToken && freshToken !== rejectedToken) {
      retryHeaders.set('Authorization', `Bearer ${freshToken}`);
      try {
        response = await fetch(resolveUrl(url), { ...init, headers: retryHeaders });
      } catch (err) {
        if (!isAbortFetchError(err) && isOfflineOrUnreachableFetchError(err)) {
          openApiDegradedCircuit('API unreachable (network/CORS)');
        }
        throw err;
      }
    }
  }
  if (response.status === 503) {
    openApiDegradedCircuit('Database unavailable (503)');
  } else if (response.status >= 502) {
    openApiDegradedCircuit(`Gateway error (${response.status})`);
  } else if (response.ok) {
    dbUnavailableUntilMs = 0;
  }
  return response;
}

/** Network / CORS / tab asleep / abort - avoid console.error spam.
 *  AbortError is quiet for logging but must not open the API degraded circuit
 *  (see fetchWithAuth) and must not trigger auth sign-out (check isAbortFetchError first). */
export function isOfflineOrUnreachableFetchError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const m = err.message || '';
  if (err.name === 'AbortError') return true;
  if (err.name === 'NetworkError') return true;
  if (err.name === 'TypeError' && /Failed to fetch|Load failed|NetworkError|Failed to load/i.test(m)) return true;
  if (/ERR_NETWORK|ERR_INTERNET|network changed/i.test(m)) return true;
  return false;
}

/** API returned 503 / DB down / pool timeout (auth or routes use Postgres). */
export function looksLikeDatabaseUnavailableError(message: string): boolean {
  return /database_unavailable|Could not connect to PostgreSQL|auth user lookup|timeout exceeded when trying to connect|\(503\)|Service Unavailable|pooler timeout|database pool is closed|server restarting|Internal auth error/i.test(
    message
  );
}
